import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LoadingSpinner } from '../shared/components';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: var(--color-gray-900);
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: var(--color-gray-600);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const GuidelinesGrid = styled.div`
  display: grid;
  gap: 2rem;
`;

const CategorySection = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  overflow: hidden;
`;

const CategoryHeader = styled.div<{ $importance: 'HIGH' | 'MEDIUM' | 'LOW' }>`
  padding: 1.5rem;
  background: ${props => {
    switch (props.$importance) {
      case 'HIGH': return 'linear-gradient(135deg, #FEE2E2, #FECACA)';
      case 'MEDIUM': return 'linear-gradient(135deg, #FEF3C7, #FDE68A)';
      default: return 'linear-gradient(135deg, #DBEAFE, #BFDBFE)';
    }
  }};
  border-bottom: 1px solid var(--color-gray-200);
`;

const CategoryTitle = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-gray-900);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ImportanceBadge = styled.span<{ $importance: 'HIGH' | 'MEDIUM' | 'LOW' }>`
  padding: 0.25rem 0.75rem;
  border-radius: var(--border-radius-full);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  ${props => {
    switch (props.$importance) {
      case 'HIGH':
        return `background: var(--color-red-100); color: var(--color-red-800);`;
      case 'MEDIUM':
        return `background: var(--color-yellow-100); color: var(--color-yellow-800);`;
      default:
        return `background: var(--color-blue-100); color: var(--color-blue-800);`;
    }
  }}
`;

const CategoryDescription = styled.p`
  margin: 0;
  color: var(--color-gray-700);
  font-size: 1rem;
  line-height: 1.5;
`;

const CategoryContent = styled.div`
  padding: 1.5rem;
`;

const ExamplesSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ExampleBox = styled.div<{ $type: 'good' | 'bad' }>`
  background: ${props => props.$type === 'good' ? 'var(--color-green-50)' : 'var(--color-red-50)'};
  border: 1px solid ${props => props.$type === 'good' ? 'var(--color-green-200)' : 'var(--color-red-200)'};
  border-radius: var(--border-radius-md);
  padding: 1rem;
`;

const ExampleTitle = styled.h4<{ $type: 'good' | 'bad' }>`
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.$type === 'good' ? 'var(--color-green-800)' : 'var(--color-red-800)'};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ExampleList = styled.ul`
  margin: 0;
  padding-left: 1rem;
`;

const ExampleItem = styled.li<{ $type: 'good' | 'bad' }>`
  color: ${props => props.$type === 'good' ? 'var(--color-green-700)' : 'var(--color-red-700)'};
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 0.5rem;
`;

const QuickTips = styled.div`
  background: linear-gradient(135deg, #EDE9FE, #DDD6FE);
  border-radius: var(--border-radius-lg);
  padding: 2rem;
  margin-top: 3rem;
`;

const TipsTitle = styled.h2`
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-gray-900);
  text-align: center;
`;

const TipsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
`;

const TipCard = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-md);
  padding: 1rem;
  box-shadow: var(--shadow-sm);
`;

const TipIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const TipText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: var(--color-gray-700);
  line-height: 1.5;
`;

interface GuidelineCategory {
  key: string;
  importance: 'HIGH' | 'MEDIUM' | 'LOW';
}

const ContentGuidelines: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const guidelineCategories: GuidelineCategory[] = [
    { key: 'ageAppropriate', importance: 'HIGH' },
    { key: 'positiveMessaging', importance: 'HIGH' },
    { key: 'language', importance: 'MEDIUM' },
    { key: 'storyStructure', importance: 'MEDIUM' },
    { key: 'creativity', importance: 'LOW' }
  ];

  if (loading) {
    return (
      <Container>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <LoadingSpinner message={t('contentGuidelines.loading')} />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{t('contentGuidelines.title')}</Title>
        <Subtitle>
          {t('contentGuidelines.subtitle')}
        </Subtitle>
      </Header>

      <GuidelinesGrid>
        {guidelineCategories.map(category => (
          <CategorySection key={category.key}>
            <CategoryHeader $importance={category.importance}>
              <CategoryTitle>
                {t(`contentGuidelines.categories.${category.key}.title`)}
                <ImportanceBadge $importance={category.importance}>
                  {t('contentGuidelines.priorityLabel', {
                    priority: t(`contentGuidelines.priority.${category.importance}`)
                  })}
                </ImportanceBadge>
              </CategoryTitle>
              <CategoryDescription>
                {t(`contentGuidelines.categories.${category.key}.description`)}
              </CategoryDescription>
            </CategoryHeader>

            <CategoryContent>
              <ExamplesSection>
                <ExampleBox $type="good">
                  <ExampleTitle $type="good">
                    <span>{t('contentGuidelines.examples.goodIcon')}</span>
                    {t('contentGuidelines.examples.goodTitle')}
                  </ExampleTitle>
                  <ExampleList>
                    {(Array.isArray(t(`contentGuidelines.categories.${category.key}.examples.good`, { returnObjects: true }))
                      ? t(`contentGuidelines.categories.${category.key}.examples.good`, { returnObjects: true }) as string[]
                      : []
                    ).map((example: string, index: number) => (
                        <ExampleItem key={index} $type="good">
                          {example}
                        </ExampleItem>
                      ))}
                  </ExampleList>
                </ExampleBox>

                <ExampleBox $type="bad">
                  <ExampleTitle $type="bad">
                    <span>{t('contentGuidelines.examples.badIcon')}</span>
                    {t('contentGuidelines.examples.badTitle')}
                  </ExampleTitle>
                  <ExampleList>
                    {(Array.isArray(t(`contentGuidelines.categories.${category.key}.examples.bad`, { returnObjects: true }))
                      ? t(`contentGuidelines.categories.${category.key}.examples.bad`, { returnObjects: true }) as string[]
                      : []
                    ).map((example: string, index: number) => (
                        <ExampleItem key={index} $type="bad">
                          {example}
                        </ExampleItem>
                      ))}
                  </ExampleList>
                </ExampleBox>
              </ExamplesSection>
            </CategoryContent>
          </CategorySection>
        ))}
      </GuidelinesGrid>

      <QuickTips>
        <TipsTitle>{t('contentGuidelines.quickTips.title')}</TipsTitle>
        <TipsList>
          {(Array.isArray(t('contentGuidelines.quickTips.tips', { returnObjects: true }))
            ? t('contentGuidelines.quickTips.tips', { returnObjects: true }) as Array<{ icon: string; text: string }>
            : []
          ).map((tip: { icon: string; text: string }, index: number) => (
              <TipCard key={index}>
                <TipIcon>{tip.icon}</TipIcon>
                <TipText>{tip.text}</TipText>
              </TipCard>
            ))}
        </TipsList>
      </QuickTips>
    </Container>
  );
};

export default ContentGuidelines;